/**
 * Clock in / clock out location. Location never blocks clocking in or out: the phone tries
 * for a fix (≤ 10 s, a fix up to 2 min old is fine) and the request is sent either way, with
 * `locationStatus` saying why there is no location. Dispatch sees it in the attendance log.
 */

export type ClockLocationStatus = 'ok' | 'unavailable' | 'denied';

export interface ClockLocationPayload {
  latitude?: number;
  longitude?: number;
  accuracyM?: number;
  locationStatus: ClockLocationStatus;
}

export interface GeoResult {
  pos: Pick<GeolocationPosition, 'coords'> | null;
  denied: boolean;
}

export const CLOCK_GEO_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  timeout: 10_000,
  maximumAge: 120_000,
};

export function readClockLocation(
  geolocation: Geolocation | undefined = typeof navigator !== 'undefined' ? navigator.geolocation : undefined,
): Promise<GeoResult> {
  return new Promise((resolve) => {
    if (!geolocation) {
      resolve({ pos: null, denied: false });
      return;
    }
    let done = false;
    const finish = (r: GeoResult) => {
      if (done) return;
      done = true;
      resolve(r);
    };
    // Some browsers never call back when the prompt is ignored — don't wait forever.
    const guard = setTimeout(() => finish({ pos: null, denied: false }), CLOCK_GEO_OPTIONS.timeout! + 1_000);
    try {
      geolocation.getCurrentPosition(
        (pos) => {
          clearTimeout(guard);
          finish({ pos, denied: false });
        },
        (err) => {
          clearTimeout(guard);
          finish({ pos: null, denied: err.code === err.PERMISSION_DENIED });
        },
        CLOCK_GEO_OPTIONS,
      );
    } catch {
      clearTimeout(guard);
      finish({ pos: null, denied: false });
    }
  });
}

export function clockLocationPayload(geo: GeoResult): ClockLocationPayload {
  if (!geo.pos) return { locationStatus: geo.denied ? 'denied' : 'unavailable' };
  const { latitude, longitude, accuracy } = geo.pos.coords;
  return {
    latitude,
    longitude,
    ...(Number.isFinite(accuracy) ? { accuracyM: accuracy } : {}),
    locationStatus: 'ok',
  };
}

/** Gentle note after a clock in/out that was recorded without a location (never an error). */
export function clockLocationNote(action: 'in' | 'out', status: ClockLocationStatus): string | null {
  if (status === 'ok') return null;
  return status === 'denied'
    ? "Location is off for this app, so it wasn't logged. Turn it on in your browser's site settings."
    : `Location wasn't available when you clocked ${action}.`;
}

/** Informational attendance line for a clocked-in crew member (never blocks anything). */
export function attendanceLocationHint(status: {
  clockedIn: boolean;
  hubGeofenceMatch?: boolean | null;
  hubDistanceM?: number | null;
  locationStatus?: string | null;
}): string | null {
  if (!status.clockedIn) return null;
  if (status.locationStatus === 'unavailable' || status.locationStatus === 'denied') {
    return clockLocationNote('in', 'unavailable');
  }
  return status.hubGeofenceMatch === false ? 'Away from the hub' : null;
}
